import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

/* ─── Types ─────────────────────────────────────────── */
interface ClientUser {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  plan: string;
  is_active: boolean;
  is_blocked: boolean;
  created_at: string;
  last_seen_at: string;
  wizard_completed: boolean;
  account_status: string | null;
  sub_status?: string;
  real_plan_name?: string;
  real_plan_slug?: string;
}

const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];

const subStatusColor: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400',
  trial: 'bg-cyan-500/20 text-cyan-400',
  expired: 'bg-orange-500/20 text-orange-400',
  cancelled: 'bg-red-500/20 text-red-400',
};
const subStatusLabel: Record<string, string> = {
  active: 'Ativa',
  trial: 'Trial',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};

/* ─── Main Page ─────────────────────────────────────── */
const AdminUsuariosPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadClients = useCallback(async () => {
    setLoading(true);
    const { data: adminRoles } = await supabase
      .from('user_roles')
      .select('user_id')
      .in('role', ADMIN_ROLES);
    const adminIds = new Set((adminRoles || []).map((r: any) => r.user_id));

    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, name, email, phone, plan, is_active, is_blocked, created_at, last_seen_at, wizard_completed, account_status')
      .order('created_at', { ascending: false });

    const clientOnly = ((data || []) as any[]).filter((p) => !adminIds.has(p.id));
    const userIds = clientOnly.map((p) => p.id);

    let subMap: Record<string, { status: string; plan_name: string; plan_slug: string }> = {};
    if (userIds.length > 0) {
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('user_id, status, plan_id, plans(name, slug), created_at')
        .in('user_id', userIds)
        .order('created_at', { ascending: false });

      // Priority: active > trial > any other (most recent wins within same priority)
      const STATUS_PRIORITY: Record<string, number> = { active: 3, trial: 2 };
      (subs || []).forEach((s: any) => {
        const existing = subMap[s.user_id];
        const incomingPriority = STATUS_PRIORITY[s.status] ?? 1;
        const existingPriority = existing ? (STATUS_PRIORITY[existing.status] ?? 1) : 0;
        if (!existing || incomingPriority > existingPriority) {
          subMap[s.user_id] = {
            status: s.status,
            plan_name: s.plans?.name || '—',
            plan_slug: s.plans?.slug || '—',
          };
        }
      });
    }

    setClients(
      clientOnly.map((p: any) => ({
        ...p,
        full_name: p.full_name || p.name || '',
        phone: p.phone || null,
        wizard_completed: p.wizard_completed ?? false,
        account_status: p.account_status || null,
        sub_status: subMap[p.id]?.status || undefined,
        real_plan_name: subMap[p.id]?.plan_name,
        real_plan_slug: subMap[p.id]?.plan_slug,
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');

  const filteredClients = clients.filter((u) => {
    const q = search.toLowerCase();
    return u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const getClientStatus = (u: ClientUser) => {
    if (u.is_blocked) return { label: 'Bloqueado', cls: 'bg-red-700/20 text-red-400' };
    if (u.is_active === false) return { label: 'Inativo', cls: 'bg-orange-500/20 text-orange-400' };
    return { label: 'Ativo', cls: 'bg-emerald-500/20 text-emerald-400' };
  };

  const getPlanBadge = (u: ClientUser) => {
    // Use real_plan_name from subscriptions → plans.name (authoritative source)
    if (u.real_plan_name && u.real_plan_name !== '—') {
      const isFree = u.real_plan_slug === 'free' || u.real_plan_name.toLowerCase() === 'free';
      if (isFree) return { label: u.real_plan_name, cls: 'bg-white/10 text-white/50' };
      if (u.sub_status === 'trial') return { label: u.real_plan_name, cls: 'bg-cyan-500/20 text-cyan-400' };
      if (u.sub_status === 'expired' || u.sub_status === 'cancelled')
        return { label: u.real_plan_name, cls: 'bg-orange-500/20 text-orange-400' };
      return { label: u.real_plan_name, cls: 'bg-amber-500/20 text-amber-400' };
    }
    // No subscription linked at all
    return { label: 'Sem plano', cls: 'bg-white/10 text-white/30' };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-700 text-white">Usuários</h1>
          <p className="text-white/40 text-[14px] mt-1">Clientes cadastrados na plataforma</p>
        </div>
        <div className="flex items-center gap-3 text-[13px] text-white/40">
          <i className="ri-information-line" />
          <span>Para gerenciar admins, use <button onClick={() => navigate('/admin/administradores')} className="text-violet-400 hover:underline cursor-pointer">Administradores</button></span>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1 max-w-sm">
          <i className="ri-search-line text-white/40 text-[16px]" />
          <input
            className="bg-transparent text-white text-[14px] outline-none placeholder:text-white/30 w-full"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-white/30 hover:text-white/60 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <span className="text-[13px] text-white/40">
          {loading ? '...' : `${filteredClients.length} cliente${filteredClients.length !== 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-white/10">
                  {['Usuário', 'Plano', 'Assinatura', 'Status', 'Cadastro', 'Último acesso', ''].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((u) => {
                  const st = getClientStatus(u);
                  const pl = getPlanBadge(u);
                  return (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/admin/clientes/${u.id}`)}
                      className="border-b border-white/5 hover:bg-violet-600/5 cursor-pointer transition-colors group"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-violet-600/20 flex items-center justify-center shrink-0">
                            <span className="text-[12px] font-700 text-violet-300">
                              {(u.full_name || u.email || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-[13px] text-white font-500 group-hover:text-violet-200 transition-colors">
                              {u.full_name || <span className="text-white/30 italic">Sem nome</span>}
                            </p>
                            <p className="text-[11px] text-white/40">{u.email || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${pl.cls}`}>{pl.label}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        {u.sub_status ? (
                          <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${subStatusColor[u.sub_status] || 'bg-white/10 text-white/40'}`}>
                            {subStatusLabel[u.sub_status] || u.sub_status}
                          </span>
                        ) : (
                          <span className="text-white/30 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-5 py-3.5 text-[12px] text-white/50">{fmt(u.created_at)}</td>
                      <td className="px-5 py-3.5 text-[12px] text-white/50">{fmt(u.last_seen_at)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1.5 text-white/30 group-hover:text-violet-400 transition-colors">
                          <span className="text-[11px]">Ver ficha</span>
                          <i className="ri-arrow-right-s-line text-lg" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredClients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <i className="ri-user-search-line text-3xl text-white/20" />
                        <p className="text-white/30 text-[13px]">
                          {search ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado ainda'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsuariosPage;
